import { useCallback, useEffect, useState } from 'react';
import {
  draftRef, archiveRef, archivesCollection,
  setDoc, onSnapshot, getDoc, getDocs, query, orderBy,
  DRAFT_YEAR, DEFAULT_LEAGUE_ID,
} from '../firebase';
import type { DraftState, ArchivedDraft, HistoryEntry } from '../types';
import { buildInitialDraftState, normalizeDraftState } from '../lib/draftLogic';

type Updater = (prev: DraftState) => DraftState;

export function useDraftState(enabled: boolean, leagueId: string = DEFAULT_LEAGUE_ID) {
  const [state, setState] = useState<DraftState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    setState(null);
    const ref = draftRef(leagueId);
    const unsub = onSnapshot(
      ref,
      async snap => {
        if (snap.exists()) {
          const raw = snap.data();
          const normalized = normalizeDraftState(raw, DRAFT_YEAR);
          // If normalization had to fill in missing fields, persist the healed doc once.
          const needsHeal =
            !Array.isArray((raw as any).availableTeams) ||
            (raw as any).availableTeams.length === 0 ||
            !Number.isFinite((raw as any).rosterCap) ||
            !Number.isFinite((raw as any).minBid);
          if (needsHeal) {
            try { await setDoc(ref, normalized); } catch { /* ignore — onSnapshot will re-fire */ }
          }
          setState(normalized);
        } else {
          // Only auto-seed the legacy/default league; other league ids must be created
          // explicitly via the LeagueSelectionScreen so managers/commissioner are set.
          if (leagueId === DEFAULT_LEAGUE_ID) {
            const initial = buildInitialDraftState(DRAFT_YEAR);
            await setDoc(ref, initial);
            setState(initial);
          } else {
            setError(`League "${leagueId}" does not exist.`);
          }
        }
        setError(null);
        setLoading(false);
      },
      err => {
        const msg =
          err.code === 'permission-denied'
            ? 'Missing or insufficient permissions. Commissioner must update Firestore Security Rules (see README).'
            : err.message;
        setError(msg);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [enabled, leagueId]);

  const writeState = useCallback(async (next: DraftState | Updater) => {
    const ref = draftRef(leagueId);
    if (typeof next === 'function') {
      // Read latest, apply, write
      const snap = await getDoc(ref);
      const cur = (snap.exists() ? normalizeDraftState(snap.data(), DRAFT_YEAR) : buildInitialDraftState(DRAFT_YEAR));
      const updated = (next as Updater)(cur);
      await setDoc(ref, updated);
    } else {
      await setDoc(ref, next);
    }
  }, [leagueId]);

  return { state, loading, error, writeState };
}

export async function archiveDraft(year: number, state: DraftState, leagueId: string = DEFAULT_LEAGUE_ID): Promise<void> {
  const payload: ArchivedDraft = {
    year,
    history: state.history,
    trades: state.trades ?? [],
    finalManagers: state.managers,
    timestamp: Date.now(),
  };
  await setDoc(archiveRef(year, leagueId), payload);
}

export async function loadPreviousYearPrices(year: number, leagueId: string = DEFAULT_LEAGUE_ID): Promise<Record<string, number>> {
  try {
    const snap = await getDoc(archiveRef(year, leagueId));
    if (!snap.exists()) return {};
    const data = snap.data() as ArchivedDraft;
    const out: Record<string, number> = {};
    data.history.forEach((h: HistoryEntry) => { out[h.team] = h.price; });
    return out;
  } catch {
    return {};
  }
}

export function useArchivedDrafts(enabled: boolean, leagueId: string = DEFAULT_LEAGUE_ID) {
  const [archives, setArchives] = useState<ArchivedDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const q = query(archivesCollection(leagueId), orderBy('year', 'desc'));
        const snap = await getDocs(q);
        const list: ArchivedDraft[] = [];
        snap.forEach(d => list.push(d.data() as ArchivedDraft));
        if (!cancelled) setArchives(list);
      } catch {
        if (!cancelled) setArchives([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, leagueId, reloadKey]);

  const refresh = useCallback(() => setReloadKey(k => k + 1), []);
  return { archives, loading, refresh };
}
